import React, {useCallback, useEffect, useState} from 'react';
import {View, ScrollView, StyleSheet, RefreshControl, Image} from 'react-native';
import {
  Text, Card, Button, Chip, Appbar, ActivityIndicator,
  Dialog, Portal, Divider,
} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import {useNavigation, useRoute} from '@react-navigation/native';
import {workerApi} from '../../services/api/workerApi';
import {workAssignmentApi} from '../../services/api/workAssignmentApi';
import {paymentApi} from '../../services/api/paymentApi';
import {colors, spacing} from '../../theme';
import {TouchableOpacity} from 'react-native';
import WorkAssignmentStatusDialog from '../order/WorkAssignmentStatusDialog';

const MODE_LABELS = {CASH: 'Cash', UPI: 'UPI', BANK_TRANSFER: 'Bank Transfer', CHEQUE: 'Cheque'};

const getMonthStartISO = () => {
  const d = new Date();
  d.setDate(1);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}-01`;
};

const getTodayISO = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const EMPLOYMENT_LABELS = {
  FULL_TIME: 'Full Time',
  PART_TIME: 'Part Time',
  CONTRACT: 'Contract',
  FREELANCER: 'Freelancer',
  DAILY_WAGE: 'Daily Wage',
  PIECE_RATE: 'Piece Rate',
  COMMISSION_BASED: 'Commission Based',
  OUTSOURCED: 'Outsourced',
  TEMPORARY: 'Temporary',
  INTERN: 'Intern',
};

const DetailRow = ({label, value}) => {
  if (!value) return null;
  return (
    <View style={styles.detailRow}>
      <Text variant="bodySmall" style={styles.detailLabel}>{label}</Text>
      <Text variant="bodyMedium" style={styles.detailValue} numberOfLines={2}>{value}</Text>
    </View>
  );
};

const WorkerDetailScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const route = useRoute();
  const {workerId} = route.params;

  const [worker, setWorker] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [dressAssignments, setDressAssignments] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [deactivateVisible, setDeactivateVisible] = useState(false);
  const [snackbar, setSnackbar] = useState('');
  const [statusDialogVisible, setStatusDialogVisible] = useState(false);
  const [statusAssignment, setStatusAssignment] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [workerRes, assignRes, dressRes, payoutsRes] = await Promise.all([
        workerApi.getById(workerId),
        workerApi.getTaskAssignments(),
        workAssignmentApi.getByWorker(workerId),
        paymentApi.getHistory({workerId, from: getMonthStartISO(), to: getTodayISO()}),
      ]);
      setWorker(workerRes.data.data);
      const allSummary = assignRes.data.data || [];
      setAssignments(allSummary.filter(a => a.workerId === workerId));
      const allDress = dressRes.data?.data || [];
      setDressAssignments(allDress.filter(a => a.status !== 'COMPLETED' && a.status !== 'PAID'));
      setPayouts(payoutsRes.data?.data || []);
    } catch (e) {
      setSnackbar(e.response?.data?.message || t('common.error'));
    }
  }, [workerId, t]);

  useEffect(() => {
    setLoading(true);
    fetchData().finally(() => setLoading(false));
  }, [fetchData]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
  }, [fetchData]);

  const handleDeactivate = useCallback(async () => {
    setDeactivateVisible(false);
    try {
      await workerApi.delete(workerId);
      navigation.goBack();
    } catch (e) {
      setSnackbar(e.response?.data?.message || t('common.error'));
    }
  }, [workerId, navigation, t]);

  const handleReactivate = useCallback(async () => {
    try {
      await workerApi.reactivate(workerId);
      await fetchData();
    } catch (e) {
      setSnackbar(e.response?.data?.message || t('common.error'));
    }
  }, [workerId, fetchData, t]);

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <Appbar.Header style={styles.appbar} dark>
        <Appbar.BackAction onPress={() => navigation.goBack()} color={colors.white} />
        <Appbar.Content title={worker?.fullName || ''} titleStyle={styles.appbarTitle} />
        <Appbar.Action
          icon="pencil"
          onPress={() => navigation.navigate('WorkerForm', {workerId})}
          color={colors.white}
        />
      </Appbar.Header>

      <ScrollView
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={handleRefresh} colors={[colors.primary]} />}>
        {worker && (
          <>
            {/* Profile Card */}
            <Card style={styles.infoCard}>
              <Card.Content style={styles.infoRow}>
                <View style={styles.avatarSection}>
                  {worker.avatarUrl ? (
                    <Image source={{uri: worker.avatarUrl}} style={styles.avatar} />
                  ) : (
                    <View style={styles.avatarFallback}>
                      <Text style={styles.avatarText}>{(worker.fullName || '?').charAt(0).toUpperCase()}</Text>
                    </View>
                  )}
                </View>
                <View style={styles.infoSection}>
                  <Text variant="headlineSmall" style={styles.name}>{worker.fullName}</Text>
                  {worker.workerCode ? (
                    <Text variant="bodySmall" style={styles.codeText}>{worker.workerCode}</Text>
                  ) : null}
                  {worker.phone && <Text variant="bodyMedium" style={styles.subtext}>{worker.phone}</Text>}
                  <View style={styles.chipRow}>
                    <Chip
                      style={[styles.statusChip, {backgroundColor: worker.isActive ? colors.success + '20' : colors.error + '20'}]}>
                      <Text style={{fontSize: 13, color: worker.isActive ? colors.success : colors.error, marginTop: 3}}>
                        {worker.isActive ? t('worker.active') : t('worker.inactive')}
                      </Text>
                    </Chip>
                    {worker.workerTypeName && (
                      <Chip style={styles.typeChip}>
                        <Text style={{fontSize: 13, color: '#000', marginTop: 3}}>{worker.workerTypeName}</Text>
                      </Chip>
                    )}
                  </View>
                </View>
              </Card.Content>
            </Card>

            {/* Details Card */}
            <Card style={styles.sectionCard}>
              <Card.Content>
                <Text variant="titleMedium" style={styles.sectionTitle}>{t('worker.details')}</Text>
                <DetailRow label={t('worker.email')} value={worker.email} />
                <DetailRow label="Employment" value={worker.employmentType ? EMPLOYMENT_LABELS[worker.employmentType] : null} />
                <DetailRow label={t('worker.primaryOrgUnit')} value={worker.primaryOrgUnitName} />
                {worker.assignedOrgUnits && worker.assignedOrgUnits.length > 0 && (
                  <View style={styles.detailRow}>
                    <Text variant="bodySmall" style={styles.detailLabel}>{t('worker.assignedOrgUnits')}</Text>
                    <View style={styles.orgChipRow}>
                      {worker.assignedOrgUnits.map(ou => (
                        <Chip key={ou.id} style={styles.orgChip}>
                          <Text style={{fontSize: 12, marginTop:2}}>{ou.name}</Text>
                        </Chip>
                      ))}
                    </View>
                  </View>
                )}
                <DetailRow label={t('worker.linkedUser')} value={worker.linkedUsername || (worker.userId ? `User #${worker.userId}` : null)} />
                <DetailRow label={t('worker.notes')} value={worker.notes} />
              </Card.Content>
            </Card>

            {/* Current Task Assignments */}
            <Card style={styles.sectionCard}>
              <Card.Content>
                <Text variant="titleMedium" style={styles.sectionTitle}>Current Task Assignments</Text>
                <Text variant="bodySmall" style={styles.sectionDesc}>Active assignments (in progress or unpaid).</Text>
                {assignments.length === 0 ? (
                  <Text variant="bodyMedium" style={styles.subtext}>{t('worker.noAssignments')}</Text>
                ) : (
                  assignments.map((a, idx) => (
                    <TouchableOpacity
                      key={`${a.taskTypeId}-${idx}`}
                      activeOpacity={0.7}
                      onPress={() => navigation.navigate('TaskAssignmentDetail', {
                        workerId: a.workerId,
                        workerName: worker?.fullName,
                        taskTypeId: a.taskTypeId,
                        taskTypeName: a.taskTypeName,
                      })}>
                      <View style={styles.assignmentRow}>
                        <View style={styles.assignmentInfo}>
                          <Text variant="bodyMedium" style={styles.taskTypeName}>{a.taskTypeName}</Text>
                        </View>
                        <Chip
                          style={{backgroundColor: colors.primary + '15', paddingVertical: 0}}>
                          <Text style={{fontSize: 13, color: '#000', marginTop: 3}}>{a.jobCount} {a.jobCount === 1 ? 'job' : 'jobs'}</Text>
                        </Chip>
                      </View>
                    </TouchableOpacity>
                  ))
                )}
              </Card.Content>
            </Card>

            {/* Current Dress Assignments */}
            <Card style={styles.sectionCard}>
              <Card.Content>
                <Text variant="titleMedium" style={styles.sectionTitle}>Current Dress Assignments</Text>
                <Text variant="bodySmall" style={styles.sectionDesc}>Pending assignments (incomplete or unpaid).</Text>
                {dressAssignments.length === 0 ? (
                  <Text variant="bodyMedium" style={styles.subtext}>No active dress assignments</Text>
                ) : (
                  dressAssignments.map((da, idx) => (
                    <View key={da.id || idx} style={styles.dressAssignmentRow}>
                      <View style={styles.dressAssignmentInfo}>
                        <Text variant="bodyMedium" style={styles.taskTypeName}>{da.catalogItemName || '-'}</Text>
                        <Text variant="bodySmall" style={styles.dressAssignmentSub}>
                          {da.instanceCode}{da.pieceRate != null ? `  \u00B7  \u20B9${Number(da.pieceRate)}` : ''}
                        </Text>
                        {da.assignedAt ? (
                          <Text variant="bodySmall" style={styles.dressAssignmentDate}>
                            {new Date(da.assignedAt).toLocaleDateString('en-IN', {month: 'short', day: 'numeric'})}
                          </Text>
                        ) : null}
                      </View>
                      <TouchableOpacity
                        activeOpacity={0.7}
                        onPress={() => {
                          setStatusAssignment(da);
                          setStatusDialogVisible(true);
                        }}>
                        <Chip style={{backgroundColor: (da.status === 'STARTED' ? colors.primary : colors.textSecondary) + '15', paddingVertical: 0}}>
                          <Text style={{fontSize: 12, color: da.status === 'STARTED' ? colors.primary : colors.textSecondary, marginTop: 3}}>
                            {da.status?.replace('_', ' ')}
                          </Text>
                        </Chip>
                      </TouchableOpacity>
                    </View>
                  ))
                )}
              </Card.Content>
            </Card>

            {/* Worker Payouts — Current Month */}
            <Card style={styles.sectionCard}>
              <Card.Content>
                <Text variant="titleMedium" style={styles.sectionTitle}>Worker Payouts</Text>
                <Text variant="bodySmall" style={styles.sectionDesc}>Payouts made this month.</Text>
                {payouts.length === 0 ? (
                  <Text variant="bodyMedium" style={styles.subtext}>No payouts this month</Text>
                ) : (
                  payouts.map((p, idx) => (
                    <View key={p.id || idx} style={styles.payoutRow}>
                      <View style={styles.payoutInfo}>
                        <Text variant="bodyMedium" style={styles.taskTypeName}>{p.transactionCode}</Text>
                        <Text variant="bodySmall" style={styles.payoutSub}>
                          {MODE_LABELS[p.paymentMode] || p.paymentMode}
                          {p.sourceType === 'BULK_PAYMENT' ? '  \u00B7  Bulk' : ''}
                          {'  \u00B7  '}{p.itemCount} item{p.itemCount !== 1 ? 's' : ''}
                        </Text>
                        {p.paymentDate ? (
                          <Text variant="bodySmall" style={styles.payoutDate}>
                            {new Date(p.paymentDate).toLocaleDateString('en-IN', {month: 'short', day: 'numeric'})}
                          </Text>
                        ) : null}
                      </View>
                      <Text style={styles.payoutAmount}>{'\u20B9'}{p.totalAmount}</Text>
                    </View>
                  ))
                )}
              </Card.Content>
            </Card>

            {/* Action Buttons */}
            <View style={styles.actionRow}>
              {worker.isActive ? (
                <Button
                  mode="outlined"
                  icon="account-off"
                  textColor={colors.error}
                  style={styles.deactivateButton}
                  onPress={() => setDeactivateVisible(true)}>
                  {t('worker.deactivate')}
                </Button>
              ) : (
                <Button
                  mode="outlined"
                  icon="account-check"
                  textColor={colors.success}
                  style={styles.reactivateButton}
                  onPress={handleReactivate}>
                  {t('worker.reactivate')}
                </Button>
              )}
            </View>
          </>
        )}
      </ScrollView>

      <Portal>
        <Dialog visible={deactivateVisible} onDismiss={() => setDeactivateVisible(false)} style={{borderRadius: 15}}>
          <Dialog.Title>{t('worker.deactivateTitle')}</Dialog.Title>
          <Dialog.Content>
            <Text>{t('worker.deactivateMessage', {name: worker?.fullName})}</Text>
            <Text variant="bodySmall" style={styles.deactivateHint}>{t('worker.deactivateHint')}</Text>
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setDeactivateVisible(false)}>{t('common.cancel')}</Button>
            <Button textColor={colors.error} onPress={handleDeactivate}>{t('worker.deactivate')}</Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>

      {statusAssignment && (
        <WorkAssignmentStatusDialog
          visible={statusDialogVisible}
          onDismiss={() => setStatusDialogVisible(false)}
          assignment={statusAssignment}
          onStatusChanged={fetchData}
        />
      )}

      {snackbar ? (
        <View style={styles.snackbarContainer}>
          <Text style={styles.snackbarText}>{snackbar}</Text>
        </View>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  appbar: {backgroundColor: colors.primary},
  appbarTitle: {color: colors.white},
  loadingContainer: {flex: 1, justifyContent: 'center', alignItems: 'center'},
  infoCard: {margin: spacing.md, backgroundColor: colors.surface, borderRadius: 12},
  infoRow: {flexDirection: 'row', alignItems: 'center', gap: 10},
  avatarSection: {width: '25%', alignItems: 'center'},
  avatar: {width: 72, height: 72, borderRadius: 36},
  avatarFallback: {
    width: 72, height: 72, borderRadius: 36, backgroundColor: colors.secondary,
    justifyContent: 'center', alignItems: 'center',
  },
  avatarText: {color: colors.white, fontSize: 28, fontWeight: 'bold'},
  infoSection: {flex: 1},
  name: {fontWeight: 'bold', color: colors.text},
  codeText: {color: colors.textSecondary, fontSize: 12, backgroundColor: colors.background, paddingHorizontal: 6, paddingVertical: 1, borderRadius: 4, alignSelf: 'flex-start', marginTop: 2},
  subtext: {color: colors.textSecondary, marginTop: 2},
  chipRow: {flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs, marginTop: spacing.xs},
  statusChip: {alignSelf: 'flex-start', height: 26, paddingVertical: 0},
  typeChip: {alignSelf: 'flex-start', backgroundColor: colors.primary + '15'},
  sectionCard: {marginHorizontal: spacing.md, marginBottom: spacing.md, backgroundColor: colors.surface, borderRadius: 12},
  sectionTitle: {fontWeight: '600', color: colors.text, marginBottom: 2},
  sectionDesc: {color: colors.textSecondary, fontSize: 12, fontStyle: 'italic', marginBottom: spacing.sm},
  detailRow: {flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: spacing.xs, borderBottomWidth: 1, borderBottomColor: colors.divider},
  detailLabel: {color: colors.textSecondary, fontSize: 12, flex: 1},
  detailValue: {color: colors.text, flex: 1.5, textAlign: 'right'},
  orgChipRow: {flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs, marginTop: 4},
  orgChip: {backgroundColor: colors.background, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0},
  assignmentRow: {
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    paddingVertical: spacing.sm, borderBottomWidth: 1, borderBottomColor: colors.divider,
  },
  assignmentInfo: {flex: 1},
  taskTypeName: {fontWeight: '600', color: colors.text},
  dressAssignmentRow: {
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    paddingVertical: spacing.sm, borderBottomWidth: 1, borderBottomColor: colors.divider,
  },
  dressAssignmentInfo: {flex: 1},
  dressAssignmentSub: {color: colors.textSecondary, fontSize: 12, marginTop: 2},
  dressAssignmentDate: {color: colors.textSecondary, fontSize: 11, marginTop: 1},
  payoutRow: {
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    paddingVertical: spacing.sm, borderBottomWidth: 1, borderBottomColor: colors.divider,
  },
  payoutInfo: {flex: 1},
  payoutSub: {color: colors.textSecondary, fontSize: 12, marginTop: 2},
  payoutDate: {color: colors.textSecondary, fontSize: 11, marginTop: 1},
  payoutAmount: {fontSize: 15, fontWeight: '700', color: colors.primary},
  actionRow: {marginHorizontal: spacing.md, marginBottom: spacing.xl},
  deactivateButton: {borderColor: colors.error},
  reactivateButton: {borderColor: colors.success},
  deactivateHint: {color: colors.textSecondary, marginTop: spacing.sm, fontStyle: 'italic'},
  snackbarContainer: {
    position: 'absolute', bottom: spacing.md, left: spacing.md, right: spacing.md,
    backgroundColor: '#333', borderRadius: 8, padding: spacing.md,
  },
  snackbarText: {color: '#fff', fontSize: 14},
});

export default WorkerDetailScreen;
